import React from 'react'
import PropTypes from 'prop-types'

import {
  ProductActionBubble,
  ProductBadges,
  BadgePill,
  Icon,
  Image,
  QuickviewBottomSheet,
  QuickviewPopup,
} from '../../../index'
import {
  getThumbnailUrl,
  getAnimalIcons,
  useVisibility,
} from '../../../../utils'
import {
  ListPagePricingProvider,
  useFeatureFlags,
  useTranslation,
  useMediaResize,
} from '../../../../utils/providers'
import ProductLink from '../../Link/ProductLink'
import ProductPrices from '../../PriceInfo/ProductPrices'
import ProductPriceHint from '../../PriceInfo/ProductPriceHint'
import { BundleProvider } from '../../ProductBundleOptions'

function ProductTile({ productData }) {
  const { t } = useTranslation()
  const { isFeatureEnabled } = useFeatureFlags()
  const { breakpoints } = useMediaResize()
  const {
    isVisible: isQuickviewVisible,
    show: showQuickview,
    hide: hideQuickview,
  } = useVisibility()

  const QuickviewComponent = breakpoints.downMD
    ? QuickviewBottomSheet
    : QuickviewPopup

  const handleImageClick = (e) => {
    if (breakpoints.downMD) {
      e.preventDefault()
      showQuickview()
    }
  }

  const showBrand =
    isFeatureEnabled('product.brand') && productData.manufacturer_name
  const showBadges = isFeatureEnabled('product.badges')
  const showVariants = isFeatureEnabled('relatedProducts')
  const isOutOfStock = !productData.availability

  const animalIcons = isFeatureEnabled('product.animals')
    ? getAnimalIcons(productData.tiericon)
    : []

  return (
    <ListPagePricingProvider productData={productData}>
      <BundleProvider productData={productData}>
        <li
          data-sku={productData.sku}
          data-selector={`pi-${productData.id}`}
          data-testid="product-listing-item"
          className="product-grid-tile"
        >
          <form className="d-flex flex-column h-100">
            {showBadges && productData.badge && (
              <ProductBadges badge={productData.badge} className="mt-1" />
            )}
            <div className="product-grid-tile__header">
              <div className="product-grid-tile__image-container position-relative">
                <ProductLink
                  href={productData.url}
                  onClick={handleImageClick}
                  className="d-flex align-items-center justify-content-center h-100"
                >
                  <Image
                    srcFull={getThumbnailUrl(productData.small_image, 400)}
                    alt={productData.name}
                    style={isOutOfStock ? { opacity: 0.5 } : null}
                  />

                  {isOutOfStock && (
                    <strong className="product-grid-tile__out-of-stock-overlay">
                      {t('OUT_OF_STOCK')}
                    </strong>
                  )}
                </ProductLink>

                {animalIcons.length > 0 && (
                  <div className="product-grid-tile__animal-icons">
                    {animalIcons.map((symbol) => (
                      <Icon key={symbol} symbol={symbol} />
                    ))}
                  </div>
                )}

                <ProductActionBubble productData={productData} />
              </div>
            </div>

            <div className="product-grid-tile__main">
              {(showBrand || productData.produkt_linie) && (
                <div className="product-grid-tile__brand">
                  {`${showBrand ? productData.manufacturer_name : ''} ${
                    productData.produkt_linie
                  }`.trim()}
                </div>
              )}

              <div className="product-grid-tile__details">
                <div className="product-grid-tile__info">
                  <ProductLink
                    className="product-grid-tile__name"
                    href={productData.url}
                    onClick={handleImageClick}
                  >
                    {productData.name}
                  </ProductLink>

                  {(productData.config_variable_a ||
                    (showVariants &&
                      productData.groupedrelations?.length > 1)) && (
                    <button
                      className="product-grid-tile__variant-container reset-btn"
                      type="button"
                      onClick={showQuickview}
                    >
                      {productData.config_variable_a && (
                        <BadgePill
                          className="p-2 font-size-sm text-wrap"
                          variant={
                            isOutOfStock ? 'disabled' : 'outline-primary'
                          }
                        >
                          {productData.config_variable_a}
                        </BadgePill>
                      )}

                      {showVariants &&
                        productData.groupedrelations?.length > 1 && (
                          <div className="text-primary text-decoration-none font-size-xs font-weight-bold">
                            {t('PRODUCT_TILE_NUM_VARIANTS')(
                              productData.groupedrelations.length - 1
                            )}
                          </div>
                        )}
                    </button>
                  )}
                </div>

                <div className="product-grid-tile__prices d-flex flex-column align-items-end justify-content-between">
                  <ProductPrices
                    productData={productData}
                    classSpecialPrice="font-size-base font-weight-bold text-right"
                    classRegularPrice="d-block text-right"
                    classUnitPrice="font-size-xxs d-block text-nowrap text-right"
                    classContainer="product-grid-tile__price_wrapper"
                  />
                  <ProductPriceHint className="font-size-xxs text-right" />
                </div>
              </div>
            </div>

            {isQuickviewVisible && (
              <QuickviewComponent
                productData={productData}
                closeAction={hideQuickview}
              />
            )}
          </form>
        </li>
      </BundleProvider>
    </ListPagePricingProvider>
  )
}

ProductTile.propTypes = {
  productData: PropTypes.shape({
    manufacturer_name: PropTypes.string,
    produkt_linie: PropTypes.string,
    availability: PropTypes.bool,
    sku: PropTypes.string,
    badge: PropTypes.string,
    small_image: PropTypes.string,
    name: PropTypes.string,
    tiericon: PropTypes.arrayOf(PropTypes.string),
    url: PropTypes.arrayOf(PropTypes.string),
    config_variable_a: PropTypes.string,
    groupedrelations: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
}

export default ProductTile
